<template>
  <div class="content-group">
    <dropdown
      :disabled="disabled"
      :items="formatItems"
      :label="label"
      :menuZIndex="isInModal ? 500 : 5"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      :rules="validationRules"
      :value="formattedValue"
      :warning="warning"
      :width="width"
      @change="[setConfigValue($event)]"
      :multiple="multiple"
    >
      <template
        v-if="!hideTooltip && (!!tooltip || !!helpContent)"
        #append-label
      >
        <tooltip v-if="tooltip" :text="tooltip" />
      </template>
    </dropdown>
    <helpIcon class="help-icon" v-if="helpContent" :content="helpContent" />
  </div>
</template>

<script>
/**
 * @fileoverview Config input field for dropdowns
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";
import dropdown from "@/qux-common/qux22/components/molecules/dropdown";
import helpIcon from "@/components/help-icon";
import tooltip from "@/components/tooltip";
import fieldMap from "./field-map.json";
import getValidationRules from "@/util/getValidationRules";
import isVisible from "@/util/isVisibleElement";

export default {
  name: "DropdownField",
  components: { dropdown, tooltip, helpIcon },
  props: {
    category: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    helpContent: {
      type: String,
    },
    hideLabel: {
      type: Boolean,
    },
    hideTooltip: {
      type: Boolean,
    },
    icon: {
      type: String,
    },
    id: {
      type: String,
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    options: {
      type: Array,
    },
    parentField: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    rules: {
      type: Array,
    },
    tooltip: {
      type: String,
    },
    value: {
      type: [String, Boolean, Number, Array],
    },
    warning: {
      type: String,
    },
    width: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      allSelected: Array.isArray(this.value) && this.value.includes("ALL"),
    };
  },
  methods: {
    /**
     * Sets the value
     */
    setConfigValue(value) {
      if (Array.isArray(value)) {
        if (value.includes("ALL")) {
          if (this.allSelected === true) {
            // Select all option was not touched but another value was unselected, unselect ALL option and select all others options
            this.allSelected = false;
            const deselectValue = value.filter((val) => val !== "ALL");
            value = this.options
              .map((option) => option.Value)
              .filter((option) => !deselectValue.includes(option));
          } else {
            // Select all option was toggled on, set to ALL
            this.allSelected = true;
            value = ["ALL"];
          }
        } else if (this.allSelected === true) {
          // Select all option was toggled off, empty our array
          this.allSelected = false;
          value = [];
        }
      }
      this.$emit("setConfigValue", value);
    },
    getFieldValue(value) {
      return (
        fieldMap[this.category] &&
        fieldMap[this.category][this.id] &&
        fieldMap[this.category][this.id][value]
      );
    },
  },
  computed: {
    formatItems() {
      const items = this.options
        .filter((option) => {
          return isVisible(
            this.accessRoles,
            this.mergedConfig[this.activeTab.ClientInfoRuleId],
            option
          );
        })
        .map((option) => ({
          text: option.Display,
          value: option.Value,
        }));
      if (this.isRequired && !this.multiple) {
        return items;
      }
      if (this.multiple) {
        if (items.length) {
          items.unshift({ text: "Select All", value: "ALL" });
        }
      } else {
        items.unshift({ text: this.placeholder, value: "" });
      }
      return items;
    },
    formattedValue() {
      let formattedValue = this.value;
      if (!Array.isArray(formattedValue)) {
        const optionsValue = this.options.find(
          (element) => element.Value == formattedValue
        );
        formattedValue = (optionsValue && optionsValue.Value) || formattedValue;
        if (this.value != formattedValue) {
          this.setConfigValue(formattedValue);
        }
      }
      return formattedValue;
    },
    isRequired() {
      return this.rules && this.rules.some((rule) => rule.Type === "required");
    },
    validationRules() {
      return getValidationRules({
        rules: this.rules,
        inputErrorMessage: this.getFieldValue("inputErrorMessage"),
      });
    },
    ...mapGetters({
      mergedConfig: "getMergedConfig",
      configTemplate: "getConfigTemplate",
      activeTab: "getActiveTab",
      accessRoles: "getRoles",
    }),
  },
};
</script>

<style lang="scss" scoped>
.content-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $qvo-spacing-8;
}

.help-icon {
  align-self: flex-start;
}
</style>